import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import CO2Service from '~/api/services/co2'
import Layout from '~/components/Layout'
import { handleErrors } from '~/utils/helpers'
import { El } from '~/components/Layout/style'
import { useForm } from 'react-hook-form'
import { FormFields } from '~/components/FormFields'
import ColorPicker from '~/components/ColorPicker'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend, ResponsiveContainer } from 'recharts';
import { Form, Tab } from 'semantic-ui-react'
import { ThemeContext } from 'styled-components'
import notify from '~/components/Notify'
import { useAuth } from '~/routes/components/AuthProvider'
import { StyledMenuItem } from '../General/style'
import { StyledTabPane } from './style'
import Perimeters from './Perimeters'

const Co2: React.FC = () => {
  const { t } = useTranslation()
  const { systemData: { languages } } = useAuth()
  const { firstColor, secondaryColor } = useContext(ThemeContext)
  const data = [
    { name: t(''), uv: 4000 },
    { name: t(''), pv: 1398 }
  ];

  const { control, handleSubmit, reset, setValue, watch, getValues, formState } = useForm({
    mode: 'onSubmit'
  })
  const watchPrimaryColor = watch('yourValueColor')
  const watchSecondaryColor = watch('bayernValueColor')

  const [isBusy, setIsBusy] = useState(false)
  const [activeTabIndex, setActiveTabIndex] = useState<string | number>(0)
  const [startingColVal, setStartingColVal] = useState({
    yourValueColor: '',
    bayernValueColor: ''
  })

  useEffect(() => {
    fetchCO2Settings()
  }, [])

  async function fetchCO2Settings(): Promise<void> {
    setIsBusy(true)
    try {
      const response = await CO2Service.getCo2Settings()
      if (response.data?.texts) {
        languages.map(language => {
          if (!response.data.texts[0]?.languages.find(lang => lang.id === language.id)) {
            response.data.texts[0].languages.push({
              id: language.id,
              translation: '',
              label: language.label
            })
          }
        })
      }
      setStartingColVal({
        yourValueColor: response.data.yourValueColor,
        bayernValueColor: response.data.bayernValueColor
      })
      reset({
        ...response.data,
        perimeters: response.data.perimeters.map(perimeter => ({ perimeter }))
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function getMenuItem(label, index) {
    const languagesErrors = formState.errors?.texts ? formState?.errors?.texts[0]?.languages : {}
    const paneHasError = index in (languagesErrors || {});
    return <StyledMenuItem
      key={label}
      className='tab-item'
    >
      <div className={`${paneHasError ? 'error' : ''}`}>{label}</div>
    </StyledMenuItem>
  }

  function renderLanguageTabs() {
    const languages = getValues('texts[0].languages')
    return <Tab
      activeIndex={activeTabIndex}
      onTabChange={(_, data) => setActiveTabIndex(data.activeIndex)}
      renderActiveOnly={false}
      menu={{ secondary: true, pointing: true }}
      panes={languages?.map((language, index) => {
        return {
          menuItem: getMenuItem(language.label, index),
          key: language.id,
          pane: <StyledTabPane
            active={index === activeTabIndex}
            key={`${language.id}-${index}`}
          >
            <FormFields.Input
              control={control}
              name={`texts[0].languages[${index}].translation`}
              label={t('Average THG Emissions Chart Text')}
              placeholder={t('Average THG Emissions Chart Text')}
              rules={{
                required: {
                  value: true,
                  message: t('Please enter Average GHG Emissions Chart Text')
                }
              }}
            />
          </StyledTabPane>
        }
      })}
    />
  }

  function renderCo2Settings(): React.ReactNode {
    return <div>
      <FormFields.Checkbox
        control={control}
        name='showLflDefaultValue'
        defaultValue={0}
        checkboxValue={1}
        label={t('Show LfL standard values ​​in the dashboard/reporting')}
      />
      <FormFields.Checkbox
        control={control}
        name='showResultsInLiveStatus'
        defaultValue={0}
        checkboxValue={1}
        label={t('Show results in live status')}
      />
      <FormFields.Checkbox
        control={control}
        name='useMilkPrice'
        defaultValue={0}
        checkboxValue={1}
        label={t('Display milk price')}
      />
      <div style={{ display: 'flex', alignItems: 'baseline', flexDirection: 'column' }}>
        <Form.Group style={{ alignItems: 'center' }}>
          <FormFields.External
            fluid
            name={'yourValueColor'}
            label={t('Primary color')}
            control={control}
            rules={{
              required: {
                value: true,
                message: t('Please enter color')
              }
            }}
          >
            <ColorPicker value={watchPrimaryColor || ''}>
              <FormFields.Input
                control={control}
                hideError
                onChange={(e, data) => setValue('yourValueColor', data.value)}
                placeholder={t('Primary color')}
                name={'yourValueColor'}
              />
            </ColorPicker>
          </FormFields.External>
          <FormFields.External
            fluid
            name={'bayernValueColor'}
            label={t('Secondary color')}
            control={control}
            rules={{
              required: {
                value: true,
                message: t('Please enter color')
              }
            }}
          >
            <ColorPicker value={watchSecondaryColor || ''}>
              <FormFields.Input
                control={control}
                hideError
                onChange={(e, data) => setValue('bayernValueColor', data.value)}
                placeholder={t('Secondary color')}
                name={'bayernValueColor'}
              />
            </ColorPicker>
          </FormFields.External>
          <El.PageHeaderActionButton
            style={{ marginTop: 22 }}
            type='button'
            onClick={() => {
              reset({
                ...getValues(),
                yourValueColor: startingColVal.yourValueColor,
                bayernValueColor: startingColVal.bayernValueColor
              })
            }}>
            {t('Reset')}
          </El.PageHeaderActionButton>
          <El.PageHeaderActionButton
            style={{ marginTop: 22 }}
            type='button'
            onClick={() => {
              reset({
                ...getValues(),
                yourValueColor: firstColor,
                bayernValueColor: secondaryColor || firstColor
              })
            }}>
            {t('Theme colors')}
          </El.PageHeaderActionButton>
        </Form.Group>
      </div>
      {renderChartColorExample()}
      {renderLanguageTabs()}
    </div>
  }

  function renderActions() {
    return <El.PageHeaderActionButton
      content={t('Update')}
    />
  }

  async function onSubmit(data) {
    setIsBusy(true);
    data.perimeters = data.perimeters.map(item => item.perimeter)
    try {
      await CO2Service.updateCo2Settings(data)
      setStartingColVal({
        yourValueColor: data.yourValueColor,
        bayernValueColor: data.bayernValueColor
      })
      notify(t('Successfully update CO2 Klima Check settings'), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false);
  }

  function renderChartColorExample() {
    return <ResponsiveContainer width='100%' height={250}>
      <BarChart
        layout='vertical' // Set the layout to vertical to align bars along the y-axis
        data={data}
        margin={{ top: 20, right: 30, left: -60, bottom: 5 }}
        maxBarSize={30}
      >
        <CartesianGrid strokeDasharray='3 3' />
        <XAxis type='number' />
        <YAxis type='category' dataKey='name' />
        <Legend />
        <Bar dataKey='uv' stackId='a' fill={watchPrimaryColor} />
        <Bar dataKey='pv' stackId='a' fill={watchSecondaryColor} />
      </BarChart>
    </ResponsiveContainer>
  }

  return <Layout
    title={<span dangerouslySetInnerHTML={{ __html: t('CO2 Klima Check') }} />}
    segmented
    isBusy={isBusy}
    actions={renderActions()}
    onSubmit={handleSubmit(onSubmit)}
  >
    <div style={{ padding: 20 }}>
      {renderCo2Settings()}
      <Perimeters
        control={control}
      />
      <FormFields.Input
        label={t('Comparison Results Count')}
        control={control}
        name='comparisonResultsNumber'
        styleControlled={{ width: 250 }}
        typeInput={'controled'}
        rules={{
          required: true
        }}
        noErrorMessage
        placeholder={t('Please enter result count')}
      />
    </div>
  </Layout>
}
export default Co2